import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, FormActions, PageHeader } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FaixaEtapasIncompletas, {
  useEtapasIncompletas,
} from '../../../components/adesao/EtapasIncompletas';
import CapturaDocumentos, {
  HandleCapturaDocumentos,
} from '../../../components/documento/CapturaDocumentos';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';

import { RootState } from '../../../store/store';
import { setAssociadoId, addCarroData } from '../../../store/associadoSlice';

import { PRODUTO } from '../../produto/routes';

import {
  fetchAssociadoPorId,
  updateAssociado,
} from '../../../api/associado/associadoApi';
import { parseMoeda } from '../../../utils/moeda';
import { rgParaEnvio } from '../../../utils/mascaras';
import { isCarroVivo } from '../../../utils/carroSituacao';

/**
 * `/documento/edit` — **etapa 3 das DUAS substituições** (at-60, frente C/C3).
 *
 * 🔴 **O nome do arquivo engana** (como em `associadoEdit`/`carroEdit`): não é
 * "editar documento". É a etapa 3 tanto da **Substituição Veicular** quanto da
 * **Substituição de Titular** — os dois fluxos convergem nesta mesma tela
 * (`plan-c` §1.4), e é ela que chama `PATCH /associado/editar-associado/:cpf`.
 *
 * ⚠️ Por servir os dois, o sobretítulo é o genérico **"Substituição"**: nomear um
 * deles seria mentir na metade dos casos, e derivar qual é pelos marcadores do
 * carro (`codigoAssociadoNovo`, `placaAntiga`) é inferência que o próprio
 * `derivarTipoRegistro` (at-28) avisa não ser exclusiva — os dois podem vir
 * preenchidos juntos.
 *
 * ⚠️ **A montagem do payload NÃO foi tocada** — inclusive os marcadores de
 * substituição, que são o que faz o backend decidir entre cadastrar e
 * `alterar/veiculo`. Ver o aviso equivalente em `documento.tsx` (achado C9).
 *
 * ## O que mudou de comportamento
 *
 *   1. **A tela ganhou "Voltar"** — não tinha cabeçalho nenhum, então só se saía
 *      pelo botão do navegador (§1.5 do `plan-c`).
 *   2. **Fim dos 3 segundos** antes de navegar para `/produto`.
 *   3. Câmera e prévia vêm do {@link CapturaDocumentos}, compartilhado com
 *      `/documento`, sem o `alert()` nativo e sem o "✖".
 *   4. **Stepper** dizendo "Etapa 3 de 6".
 */
const DocumentoEditPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const captura = useRef<HandleCapturaDocumentos>(null);
  const [gravando, setGravando] = useState(false);

  /*
   * at-62: etapa anterior incompleta trava a captura e a atualização — mesmo
   * motivo da adesão nova (ver `components/adesao/EtapasIncompletas`), e aqui o
   * `PATCH` também recusaria o payload sem os campos obrigatórios.
   */
  const { bloqueado } = useEtapasIncompletas(3);

  const volunteerCode = useSelector((state: RootState) => state.auth.codigoAssociado);
  const associado = useSelector((state: RootState) => state.associado);
  const codigo = useSelector((state: any) => state.codigo);

  // at-30: parse centralizado por estrutura — o antigo /\D/g + /100 virava 1/100
  // do valor quando a string não estava mascarada com 2 casas
  const parseCurrency = (value: string) => parseMoeda(value);

  const concluirAtualizacao = async () => {
    if (!associado.carro) {
      toast.error('Dados do veículo não encontrados. Volte e preencha o veículo.');
      return;
    }

    setGravando(true);
    try {
      const associadoData = {
        nomeCompleto: associado.nomeCompleto,
        cpfCnpj: associado.cpfCnpj.replace(/\D/g, ''),
        // at-58 (D5): preserva as letras do RG — as duas telas de substituição
        // passam por aqui, e o formulário delas já aceita RG alfanumérico.
        // ⚠️ Tem de ser a MESMA regra de `documento.tsx`: divergir gravaria o
        // mesmo RG de duas formas numa coluna `@unique`.
        rg: rgParaEnvio(associado.rg),
        dataNascimento: associado.dataNascimento,
        sexo: (associado.sexo ?? '').toString().trim().toUpperCase(),
        email: associado.email,
        dddCelular: associado.dddCelular,
        celular: associado.celular,
        regional: associado.regionalId,
        rua: associado.rua,
        numero: associado.numero,
        complemento: associado.complemento,
        bairro: associado.bairro,
        cidade: associado.cidade,
        estado: associado.estado,
        cep: associado.cep,
        volunteerCode: volunteerCode,
        carro: {
          // Motocicleta elétrica (at-46): o id do catálogo liga o modo bike no backend
          ...(associado.carro.bikeEletricaId
            ? { bikeEletricaId: associado.carro.bikeEletricaId }
            : {}),
          // at-81: substituição por um veículo 0km (sem placa nem renavam).
          ...(associado.carro.zeroKm && !associado.carro.bikeEletricaId ? { zeroKm: true } : {}),
          placa: associado.carro.placa,
          anoModelo: associado.carro.anoModelo,
          anoFabricacao: associado.carro.anoFabricacao,
          modelo: associado.carro.modelo,
          chassi: associado.carro.chassi,
          renavam: associado.carro.renavam,
          cooperativa: codigo.codigoCooperativa,
          cor: associado.carro.corId,
          combustivel: associado.carro.combustivelId,
          cambio: associado.carro.cambioId,
          tipoVeiculo: codigo.codigoTipoVeiculo,
          categoria: associado.carro.categoriaId,
          codigoFipe: associado.carro.codigoFipe,
          // NaN (bike sem FIPE) vira undefined — omitido do JSON
          valorFipe: Number.isFinite(parseCurrency(associado.carro.valorFipe))
            ? parseCurrency(associado.carro.valorFipe)
            : undefined,
          valorProtegido: parseCurrency(associado.carro.valorProtegido),
          cota: associado.carro.cotaId,
          vencimentoDesejado: associado.carro.vencimentoDesejado,
          valorCota: Number(associado.carro.valorCota ?? 0),

          placaAntiga: associado.carro.placaAntiga ?? associado.carro.placaSubstituido ?? null,
          codigoVeiculoAntigo:
            associado.carro.codigoVeiculoAntigo ??
            associado.carro.codigoVeiculoSubstituido ??
            null,

          ...(associado.carro.codigoAssociadoNovo != null
            ? { codigoAssociadoNovo: String(associado.carro.codigoAssociadoNovo) }
            : {}),

          placaSubstituido: associado.carro.placaSubstituido ?? null,
          codigoVeiculoSubstituido: associado.carro.codigoVeiculoSubstituido ?? null,

          endereco: {
            rua: associado.rua,
            numero: associado.numero,
            complemento: associado.complemento,
            bairro: associado.bairro,
            cidade: associado.cidade,
            estado: associado.estado,
            cep: associado.cep,
          },
        },
      };

      const responseCreateAssociado = await updateAssociado(associadoData);

      const associadoId = responseCreateAssociado?.associado?.id;
      if (!associadoId) {
        throw new Error('Update não confirmou no backend (sem associado.id).');
      }

      dispatch(setAssociadoId(associadoId));
      dispatch(addCarroData({ codigoAssociadoNovo: null }));

      // Documentos com dono: o carro resultante da substituição é conhecido só
      // agora (o update pode ter criado um veículo novo).
      let carroId: string | undefined;
      try {
        const associadoSalvo = await fetchAssociadoPorId(associadoId);
        const vivos = (associadoSalvo?.carros ?? []).filter(isCarroVivo);
        carroId = vivos[0]?.id;
        // at-81: substituição por um 0km — o backend gerou a identificação provisória
        // pelo chassi. Acha o veículo novo pelo CHASSI (o slice não tem placa) e grava
        // a identificação no fluxo, que é por onde `/termo` acha o veículo.
        if (associado.carro.zeroKm) {
          const chassi = String(associado.carro.chassi ?? '').replace(/\s+/g, '').toUpperCase();
          const novo = vivos.find((c: any) => String(c.chassi ?? '').toUpperCase() === chassi);
          if (novo) {
            carroId = novo.id;
            dispatch(addCarroData({ placa: novo.placa }));
          }
        }
      } catch {
        // segue sem o carroId: documento com dono parcial é melhor que órfão
      }

      await captura.current?.enviarDocumentos(associadoId, carroId);

      toast.success('Associado e veículo atualizados com sucesso', {
        autoClose: 5000,
        closeOnClick: true,
        theme: 'colored',
      });

      // at-36 (item 25): o backend já tentou enviar o veículo ao SGA. Só avisa quando
      // ficou pendente — no caminho feliz o toast acima basta.
      const envioSga = responseCreateAssociado?.envioSga;
      if (envioSga && envioSga.status !== 'ENVIADO') {
        toast.info(
          'A atualização foi salva. O envio ao SGA será feito automaticamente em instantes.',
          { autoClose: 7000, closeOnClick: true },
        );
      }

      // ⚠️ Sem `setTimeout`. O toast sobrevive à navegação (container único).
      navigate(PRODUTO());
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(axiosError.response?.data?.message || 'Erro ao finalizar a atualização.');
      setGravando(false);
    }
  };

  return (
    <ShellSaac
      cabecalho={<PageHeader titulo="Documentos" sobretitulo="Substituição" aoVoltar={voltar} />}
    >
      {gravando && <FullScreenLoader mensagem="Atualizando associado e veículo..." />}

      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={3} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              {/*
                ⚠️ `fluxo={null}`: esta tela serve as DUAS substituições e não tem
                como saber qual está em curso, então a faixa não oferece o atalho
                — o caminho de volta é o "Voltar" do cabeçalho.
              */}
              <FaixaEtapasIncompletas etapa={3} fluxo={null} />

              <CapturaDocumentos
                ref={captura}
                prefixoFalhaUpload="A atualização foi salva"
                desabilitado={gravando || bloqueado}
              />

              <FormActions
                apoio={
                  bloqueado
                    ? 'Complete as etapas anteriores para atualizar.'
                    : 'As fotos são opcionais e podem ser reenviadas depois.'
                }
              >
                <Botao
                  variante="primario"
                  larguraTotal
                  carregando={gravando}
                  disabled={bloqueado}
                  onClick={() => void concluirAtualizacao()}
                >
                  {gravando ? 'Atualizando...' : 'Atualizar Associado e Veículo'}
                </Botao>
              </FormActions>
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default DocumentoEditPage;
